import { ReactNode, Suspense, useState } from 'react';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  IconAngleDown,
  IconAngleUp,
  IconCopyOutline,
  IconEyeHiddenFilled,
} from '@iwoca/icons';
import { Skeleton } from '@iwoca/orion';

import { FunctionalityWidget } from './FunctionalityWidget';
import { useSuspenseGetStateByStateKey } from '../../api/lending/lapiHooks.suspense';
import { CustomErrorBoundary } from '../../AppWrapper';
import { useQueryParam } from '../../hooks/useQueryParam';
import { getRuntimeEnvironment } from '../../utils/getRuntimeEnvironment';
import { copy } from '../util/copy';
import { useSuspenseIsImpersonating } from './hooks/useSuspenseIsImpersonating';

const coloursForEnvironment = (environment: string) => {
  switch (environment) {
    case 'LOCAL':
      return {
        primary: { background: 'bg-primary-40', text: 'text-white' },
        secondary: { background: 'bg-[#A9C8EF]', text: 'text-primary-20' },
      };
    case 'STAGING':
      return {
        primary: { background: 'bg-warning', text: 'text-black' },
        secondary: { background: 'bg-[#FFE699]', text: 'text-[#6D5700]' },
      };
    default:
      return {
        primary: { background: 'bg-core-40', text: 'text-white' },
        secondary: { background: 'bg-core-90', text: 'text-core-40' },
      };
  }
};

export const ImpersonationBanner = () => {
  const stateKeyParam = useQueryParam('state_key');
  if (!stateKeyParam) return <></>;

  // we use this outer suspense to make sure that no loading state is shown
  // if they are not impersonating e.g. an Access User
  return (
    <CustomErrorBoundary errorComponent={<></>}>
      <Suspense fallback={<></>}>
        <ImpersonationBannerOuter />
      </Suspense>
    </CustomErrorBoundary>
  );
};

const ImpersonationBannerOuter = () => {
  const { isImpersonating, impersonationStateKey } =
    useSuspenseIsImpersonating();

  if (!isImpersonating) return <></>;

  return (
    <CustomErrorBoundary
      errorComponent={
        <ImpersonationBannerShell
          companyName={'(Unable to get company name)'}
          environment={getRuntimeEnvironment()}
          stateKey={impersonationStateKey!}
        />
      }
    >
      <Suspense
        fallback={
          <ImpersonationBannerShell
            companyName={<Skeleton size={100} />}
            environment={<Skeleton size={50} />}
            stateKey={impersonationStateKey!}
          />
        }
      >
        <ImpersonationBannerInner stateKey={impersonationStateKey!} />
      </Suspense>
    </CustomErrorBoundary>
  );
};

const ImpersonationBannerInner = ({ stateKey }: { stateKey: string }) => {
  const { state: customerState } = useSuspenseGetStateByStateKey();
  const companyName =
    customerState.application?.company?.registered_company_name ?? 'customer';
  const environment = getRuntimeEnvironment();

  return (
    <ImpersonationBannerShell
      stateKey={stateKey}
      companyName={companyName}
      environment={environment}
    />
  );
};

const ImpersonationBannerShell = ({
  companyName,
  environment,
  stateKey,
}: {
  companyName: ReactNode;
  environment: ReactNode;
  stateKey: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const toggleVisibility = () => setIsOpen((prev) => !prev);
  const copyStateKey = () => {
    toggleVisibility();
    copy(stateKey);
  };
  const hideBanner = () => {
    toggleVisibility();
    setIsHidden(true);
  };

  const colours = coloursForEnvironment(String(environment));
  return (
    <Menu>
      <MenuButton
        onClick={toggleVisibility}
        onMouseEnter={() => isHidden && setIsHidden(false)}
        className={`fixed right-s top-s z-50 bg-transparent p-0 outline-none transition-transform ${isHidden ? 'right-0 translate-x-[calc(100%-15px)]' : 'right-s'}`}
      >
        <div className="overflow-hidden rounded-2xl [backdrop-filter:blur(2px)]">
          <div
            className={`${colours.primary.background} ${colours.primary.text} px-xl py-m text-left text-[8px] opacity-70 outline-none transition-opacity hover:opacity-100`}
          >
            <p className="m-0 p-0">{environment}</p>
            <div className="flex items-center gap-s text-xs">
              <div className="flex gap-s">Impersonating {companyName}</div>{' '}
              {!isOpen ? <IconAngleDown /> : <IconAngleUp />}
            </div>
          </div>
        </div>
      </MenuButton>

      <MenuItems
        anchor="bottom end"
        className={`mt-s rounded-2xl px-m text-xs ${colours.secondary.background} ${colours.secondary.text} outline-none`}
      >
        <MenuItem>
          <FunctionalityWidget />
        </MenuItem>
        <MenuItem>
          <button
            className={`flex items-center gap-s bg-transparent px-s py-0 ${colours.secondary.text}`}
            onClick={copyStateKey}
          >
            <IconCopyOutline />
            <p>Copy state key</p>
          </button>
        </MenuItem>
        <MenuItem>
          <button
            className={`flex items-center gap-s bg-transparent px-s py-0 ${colours.secondary.text}`}
            onClick={hideBanner}
          >
            <IconEyeHiddenFilled />
            <p>Hide</p>
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
};
